import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Plus, 
  Edit, 
  Trash2, 
  DollarSign, 
  Check, 
  X, 
  Star,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

interface PricingPackage {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  features: string[];
  duration: string;
  isPopular: boolean;
  isActive: boolean;
  order: number;
}

interface PricingFormData {
  name: string;
  description: string;
  price: number;
  currency: string;
  features: string;
  duration: string;
  isPopular: boolean;
  isActive: boolean;
}

const pricingSchema = yup.object({
  name: yup
    .string()
    .required('Package name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name cannot exceed 100 characters'),
  
  description: yup
    .string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description cannot exceed 500 characters'),
  
  price: yup
    .number()
    .required('Price is required')
    .min(0, 'Price must be positive')
    .max(999999, 'Price is too high'),
  
  currency: yup
    .string()
    .required('Currency is required'),
  
  features: yup
    .string()
    .required('Features are required'),
  
  duration: yup
    .string()
    .required('Duration is required'),
  
  isPopular: yup
    .boolean(),
  
  isActive: yup
    .boolean()
});

const PricingManagement: React.FC = () => {
  const [packages, setPackages] = useState<PricingPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPackage, setEditingPackage] = useState<PricingPackage | null>(null);
  const [showForm, setShowForm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(pricingSchema),
    defaultValues: {
      currency: 'USD',
      isPopular: false,
      isActive: true,
      duration: 'one-time'
    }
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/pricing`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pricing packages');
      }

      const result = await response.json();
      setPackages(result.data.packages || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      // Set some sample data for now
      setPackages([
        {
          id: '1',
          name: 'Basic Website',
          description: 'Perfect for small businesses and personal websites',
          price: 899,
          currency: 'USD',
          features: ['Responsive design', '5 pages', 'Contact form', 'Basic SEO', '1 month support'],
          duration: 'one-time',
          isPopular: false,
          isActive: true,
          order: 1
        },
        {
          id: '2',
          name: 'Professional Website',
          description: 'Ideal for growing businesses with advanced features',
          price: 1599,
          currency: 'USD',
          features: ['Everything in Basic', '10 pages', 'Blog setup', 'E-commerce ready', 'Advanced SEO', '3 months support'],
          duration: 'one-time',
          isPopular: true,
          isActive: true,
          order: 2
        },
        {
          id: '3',
          name: 'Enterprise Solution',
          description: 'Custom solution for large businesses and organizations',
          price: 2999,
          currency: 'USD',
          features: ['Everything in Professional', 'Unlimited pages', 'Custom functionality', 'Performance optimization', '6 months support'],
          duration: 'one-time',
          isPopular: false,
          isActive: true,
          order: 3
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      const token = localStorage.getItem('adminToken');
      const packageData = {
        ...data,
        features: data.features.split('\n').map((feature: string) => feature.trim()).filter(Boolean),
        order: editingPackage ? editingPackage.order : packages.length + 1
      };

      const url = editingPackage
        ? `${process.env.REACT_APP_API_URL}/admin/pricing/${editingPackage.id}`
        : `${process.env.REACT_APP_API_URL}/admin/pricing`;
      
      const method = editingPackage ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(packageData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to ${editingPackage ? 'update' : 'create'} pricing package`);
      }

      alert(`Pricing package ${editingPackage ? 'updated' : 'created'} successfully!`);
      setShowForm(false);
      setEditingPackage(null);
      reset();
      fetchPackages();

    } catch (error) {
      console.error('Error saving pricing package:', error);
      alert(error instanceof Error ? error.message : 'Failed to save pricing package');
    }
  };

  const handleEdit = (pkg: PricingPackage) => {
    setEditingPackage(pkg);
    reset({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price,
      currency: pkg.currency,
      features: pkg.features.join('\n'),
      duration: pkg.duration,
      isPopular: pkg.isPopular,
      isActive: pkg.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (packageId: string) => {
    if (!window.confirm('Are you sure you want to delete this pricing package?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/pricing/${packageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete pricing package');
      }

      alert('Pricing package deleted successfully!');
      fetchPackages();

    } catch (error) {
      console.error('Error deleting package:', error);
      alert('Failed to delete pricing package');
    }
  };

  const toggleActive = async (packageId: string) => {
    try {
      const pkg = packages.find(p => p.id === packageId);
      if (!pkg) return;

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/pricing/${packageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ ...pkg, isActive: !pkg.isActive }),
      });

      if (!response.ok) {
        throw new Error('Failed to update package status');
      }

      fetchPackages();

    } catch (error) {
      console.error('Error toggling package status:', error);
      alert('Failed to update package status');
    }
  };

  const movePackage = async (packageId: string, direction: 'up' | 'down') => {
    const currentIndex = packages.findIndex(p => p.id === packageId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= packages.length) return;

    const reorderedPackages = [...packages];
    const [movedPackage] = reorderedPackages.splice(currentIndex, 1);
    reorderedPackages.splice(newIndex, 0, movedPackage);

    // Update order values
    const updatedPackages = reorderedPackages.map((pkg, index) => ({
      ...pkg,
      order: index + 1
    }));

    setPackages(updatedPackages);

    // Save new order to backend
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${process.env.REACT_APP_API_URL}/admin/pricing/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          packages: updatedPackages.map(pkg => ({ id: pkg.id, order: pkg.order }))
        }),
      });
    } catch (error) {
      console.error('Error updating package order:', error);
      fetchPackages(); // Revert on error
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPackage(null);
    reset();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pricing Management</h1>
          <p className="text-gray-600">Manage your service packages and pricing</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Package
        </button>
      </div>

      {/* Pricing Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingPackage ? 'Edit Pricing Package' : 'Add New Pricing Package'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package Name *
                  </label>
                  <input
                    {...register('name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Basic Website"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price *
                    </label>
                    <input
                      {...register('price', { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="999"
                    />
                    {errors.price && (
                      <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency *
                    </label>
                    <select
                      {...register('currency')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  {...register('description')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of this package..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Features * (one per line)
                </label>
                <textarea
                  {...register('features')}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={`Responsive design\n5 pages\nContact form\nBasic SEO\n1 month support`}
                />
                {errors.features && (
                  <p className="mt-1 text-sm text-red-600">{errors.features.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration *
                </label>
                <select
                  {...register('duration')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="one-time">One-time payment</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <input
                    {...register('isPopular')}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Mark as Popular
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    {...register('isActive')}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingPackage ? 'Update Package' : 'Create Package'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pricing Packages List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Current Packages</h2>
        </div>

        {packages.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pricing packages</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new pricing package.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Package
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {packages
              .sort((a, b) => a.order - b.order)
              .map((pkg) => (
                <div
                  key={pkg.id}
                  className={`p-6 ${!pkg.isActive ? 'bg-gray-50 opacity-60' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900">
                          {pkg.name}
                        </h3>
                        {pkg.isPopular && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Star className="h-3 w-3 mr-1" />
                            Popular
                          </span>
                        )}
                        {!pkg.isActive && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </div>
                      
                      <p className="mt-1 text-sm text-gray-600">{pkg.description}</p>
                      
                      <div className="mt-2 flex items-center space-x-4">
                        <span className="text-2xl font-bold text-gray-900">
                          ${pkg.price}
                        </span>
                        <span className="text-sm text-gray-500">
                          {pkg.duration === 'one-time' ? 'One-time' : `Per ${pkg.duration.slice(0, -2)}`}
                        </span>
                      </div>

                      <div className="mt-3">
                        <ul className="text-sm text-gray-600 space-y-1">
                          {pkg.features.slice(0, 3).map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              {feature}
                            </li>
                          ))}
                          {pkg.features.length > 3 && (
                            <li className="text-gray-500">
                              +{pkg.features.length - 3} more features
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Reorder buttons */}
                      <div className="flex flex-col space-y-1">
                        <button
                          onClick={() => movePackage(pkg.id, 'up')}
                          disabled={pkg.order === 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => movePackage(pkg.id, 'down')}
                          disabled={pkg.order === packages.length}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                      </div>

                      <button
                        onClick={() => toggleActive(pkg.id)}
                        className={`p-2 rounded-lg ${
                          pkg.isActive
                            ? 'text-red-600 hover:bg-red-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={pkg.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {pkg.isActive ? (
                          <X className="h-4 w-4" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                      </button>

                      <button
                        onClick={() => handleEdit(pkg)}
                        className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                        title="Edit Package"
                      >
                        <Edit className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(pkg.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete Package"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingManagement;